import TextWithIcon, { TextWithIconProps } from "common/Text/TextWithIcon";
import TextWithPrecision from "common/Text/TextWithPrecision";
import { PrecisionConfig } from "@shared/types";
import React from "react";
import { useTheme } from "styled-components";
import { ICON_TYPES } from "@shared/common/constants";
import { RootState } from "app/store";
import { useTextWithPrecisionProps } from "@shared/hooks/text/useTextWithPrecision";
import { round as roundNumber, toFixed } from "@shared/utils/numbers";

export interface TextWithPrecisionAndIconProps
  extends Omit<TextWithIconProps, "text">,
    useTextWithPrecisionProps {
  /** New look: write "+" before positive amounts (PnL figures). */
  showPlus?: boolean;
  precisionSelector?: (state: RootState) => PrecisionConfig | {} | undefined;
}

const TextWithPrecisionAndIcon = ({
  text,
  precisionSelector,
  precisionConfig,
  precisionType,
  precisionCoin,
  iconType,
  iconStyle,
  iconSize,
  exchange,
  coinOrExchangeName,
  justify,
  currency,
  round = false,
  withFallback = false,
  precision,
  style,
  textStyle,
  showPlus,
}: TextWithPrecisionAndIconProps) => {
  const { isNewLook } = useTheme();
  // A figure shown as 0 at its precision ("0.00") takes no sign. (A 0
  // precision falls back to the configured one, so it can't be judged here.)
  const isShownAsZero =
    isNewLook &&
    !!precision &&
    !withFallback &&
    text !== "" &&
    Number.isFinite(Number(text)) &&
    !(round ? roundNumber : toFixed)(Math.abs(Number(text)), precision);
  // The new look writes negative amounts as "−$29.48", not "$-29.48".
  const isSignedCurrency =
    isNewLook &&
    !isShownAsZero &&
    (iconType ?? ICON_TYPES.DISPLAY_CURRENCY) === ICON_TYPES.DISPLAY_CURRENCY &&
    Number(text) < 0;
  return (
    <TextWithIcon
      sign={
        isSignedCurrency
          ? "\u2212"
          : isNewLook && showPlus && !isShownAsZero && Number(text) > 0
          ? "+"
          : undefined
      }
      iconType={iconType}
      iconStyle={iconStyle}
      iconSize={iconSize}
      coinOrExchangeName={coinOrExchangeName}
      justify={justify}
      currency={currency}
      style={style}
      textStyle={textStyle}
      text={
        <TextWithPrecision
          text={
            isSignedCurrency || isShownAsZero ? Math.abs(Number(text)) : text
          }
          precisionSelector={precisionSelector}
          precisionType={precisionType}
          precisionCoin={precisionCoin}
          exchange={exchange}
          round={round}
          precisionConfig={precisionConfig}
          withFallback={withFallback}
          precision={precision}
        />
      }
    />
  );
};

export default React.memo(TextWithPrecisionAndIcon);
